// Shared VishCoder Chat Panel - can be used in any tab
import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useVishCoderChat } from './useVishCoderChat';
import {
    ChatPage,
    ChatMessage,
    MAX_STORED_STREAM_ACTIVITY_LOGS,
    effectiveChatPage,
    isStoredStreamActivityRow,
    markChatMessageActionsUsed,
    PAYLOAD_MESSAGE_ACTIONS_MSG_TYPE,
} from './sharedChatService';
import { summariseAiGroup } from '../../build_feature/components/ai-activity-utils';
import { Attachment, ImageAttachment, PendingAttachment, ChatAttachment } from './shared/attachements/conversationalTypes';
import { uploadImage, uploadScreenshotFromCache, validateImageFile, convertDataURLToBlob, createImageThumbnailDataURL, getImageFilesFromClipboardEvent } from './shared/attachements/imageUploadUtils';
import { getScreenshotFromCache } from '../../../../../shared/ui/ScreenshotCapture';
import { TokenManager } from '../../../../../shared/lib/apiClient';
import { useWebSocketStatus } from '../../../../../shared/lib/comm/gateway_ws/useWebSocketStatus';
import { useBottomPanelInset } from '../../build_feature/components/bottom-panel-inset-context';
import { useDiscoveryProceedPauseApi } from '../../discovery/DiscoveryProceedPauseContext';
import { ChatMarkdownBody } from './ChatMarkdownBody';

interface VishCoderChatPanelProps {
    isOpen: boolean;
    onToggle: () => void;
    page?: ChatPage;
    projectId?: number | string | null;
    channel?: 'chat' | 'discovery';
    className?: string;
}

function extractS3PathFromUrl(url?: string): string | undefined {
    if (!url) return undefined;
    if (url.startsWith('s3://')) {
        const raw = url.replace('s3://', '').trim();
        if (!raw) return undefined;
        const slashIdx = raw.indexOf('/');
        return slashIdx >= 0 ? raw.slice(slashIdx + 1) : undefined;
    }
    try {
        const parsed = new URL(url);
        const host = parsed.hostname;
        const pathname = parsed.pathname.replace(/^\/+/, '');
        if (!pathname) return undefined;
        if (host.includes('.s3.') || host.includes('.s3-')) {
            return decodeURIComponent(pathname);
        }
        if (host.startsWith('s3.')) {
            const slashIdx = pathname.indexOf('/');
            if (slashIdx >= 0) {
                return decodeURIComponent(pathname.slice(slashIdx + 1));
            }
        }
    } catch {
        // Ignore invalid URL; try generic fallback.
    }
    const match = url.match(/https?:\/\/[^/]+\/(.+)/);
    return match?.[1] ? decodeURIComponent(match[1]) : undefined;
}

/** Same session as the open panel: channel, tab page, project, and backend user when present. */
function chatMessageMatchesPanelSession(
    m: ChatMessage,
    panelChannel: 'chat' | 'discovery',
    panelPage: ChatPage | undefined,
    panelProjectId: number | string | null | undefined,
    localUserId: string | null
): boolean {
    if (m.channel !== panelChannel) return false;
    const msgPage = effectiveChatPage(m);
    if (panelPage && msgPage !== panelPage) return false;
    if (panelProjectId != null && String(panelProjectId).trim() !== '') {
        const p = String(panelProjectId);
        if (m.project_id != null && String(m.project_id).trim() !== '' && String(m.project_id) !== p) {
            return false;
        }
    }
    if (localUserId && m.backend_user_id && String(m.backend_user_id) !== localUserId) {
        return false;
    }
    return true;
}

/**
 * Payload-action buttons are enabled only for the current offer: the newest payload-actions
 * assistant that is not superseded by a user turn, a final assistant `text` reply, or a newer
 * non–stream-assistant row. Trailing status lines and thinking/tool_usage stream rows are ignored
 * so a payload-actions card stays clickable while AI activity streams below it.
 */
function getActivePayloadActionsMessageId(sessionTranscript: ChatMessage[]): string | null {
    for (let i = sessionTranscript.length - 1; i >= 0; i--) {
        const m = sessionTranscript[i];
        if (m.type === 'status') continue;
        if (isStoredStreamActivityRow(m)) continue;
        if (m.stream_activity_category) continue;

        if (m.type === 'user') return null;

        if (
            m.type === 'assistant' &&
            m.msgType === PAYLOAD_MESSAGE_ACTIONS_MSG_TYPE &&
            m.message_actions &&
            m.message_actions.length > 0
        ) {
            return m.id;
        }
        if (m.type === 'assistant' && m.msgType === 'text') return null;
        return null;
    }
    return null;
}

/** Safely format timestamp - returns fallback for invalid/unknown dates. Includes date and time. */
function formatTimestamp(ts: Date | string | number | undefined, options?: { timeOnly?: boolean }): string {
    const d = ts instanceof Date ? ts : ts != null ? new Date(ts as string | number) : new Date(0);
    if (isNaN(d.getTime()) || d.getTime() === 0) return '—';
    if (options?.timeOnly) {
        return d.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        });
    }
    return d.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });
}

/** Shown on sent user messages that included one or more attachments. */
const AttachmentSentIndicator: React.FC<{ count: number; className?: string }> = ({
    count,
    className = 'text-purple-600',
}) => (
    <span
        className={`inline-flex shrink-0 items-center ${className}`}
        title={`${count} attachment${count === 1 ? '' : 's'} sent`}
        aria-label={`${count} attachment${count === 1 ? '' : 's'} sent with this message`}
    >
        <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
            />
        </svg>
    </span>
);

/** Newest stream lines shown without expanding "Earlier" (Progress-tab-style: one latest). */
const CHAT_AI_ACTIVITY_VISIBLE_TAIL = 1;

function isStreamActivityChatMessage(m: ChatMessage): boolean {
    const c = m.stream_activity_category;
    return c === 'ai_thinking' || c === 'ai_tool_usage';
}

type ChatDisplayItem =
    | { kind: 'message'; message: ChatMessage }
    | { kind: 'stream_group'; id: string; messages: ChatMessage[] };

function buildChatDisplayItems(rows: ChatMessage[]): ChatDisplayItem[] {
    const items: ChatDisplayItem[] = [];
    let pending: ChatMessage[] = [];

    const flush = () => {
        if (pending.length > 0) {
            items.push({
                kind: 'stream_group',
                id: `stream-group-${pending[0].id}`,
                messages: [...pending],
            });
            pending = [];
        }
    };

    for (const m of rows) {
        if (isStreamActivityChatMessage(m)) pending.push(m);
        else {
            flush();
            items.push({ kind: 'message', message: m });
        }
    }
    flush();
    return items;
}

function lastMessageInDisplayItem(item: ChatDisplayItem): ChatMessage | undefined {
    if (item.kind === 'message') return item.message;
    const ms = item.messages;
    return ms.length ? ms[ms.length - 1] : undefined;
}

function streamActivityIcon(m: ChatMessage): string {
    return m.stream_activity_category === 'ai_tool_usage' ? '🔧' : '💭';
}

function streamActivityTextClass(m: ChatMessage): string {
    return m.stream_activity_category === 'ai_tool_usage'
        ? 'text-amber-500/80'
        : 'text-slate-500';
}

const ChatStreamActivityGroup: React.FC<{ messages: ChatMessage[] }> = ({ messages }) => {
    const { olderMessages, tailMessages } = useMemo(() => {
        if (messages.length <= CHAT_AI_ACTIVITY_VISIBLE_TAIL) {
            return { olderMessages: [] as ChatMessage[], tailMessages: messages };
        }
        const olderFull = messages.slice(0, -CHAT_AI_ACTIVITY_VISIBLE_TAIL);
        const olderCapped =
            olderFull.length > MAX_STORED_STREAM_ACTIVITY_LOGS
                ? olderFull.slice(-MAX_STORED_STREAM_ACTIVITY_LOGS)
                : olderFull;
        return {
            olderMessages: olderCapped,
            tailMessages: messages.slice(-CHAT_AI_ACTIVITY_VISIBLE_TAIL),
        };
    }, [messages]);

    const [earlierExpanded, setEarlierExpanded] = useState(false);
    const toggleEarlier = useCallback(() => setEarlierExpanded((v) => !v), []);
    const groupTimestamp = messages.length > 0 ? messages[0].timestamp : undefined;

    const headerSummaryLabel = useMemo(
        () =>
            summariseAiGroup(
                messages.map((m) => ({
                    category: m.stream_activity_category,
                    content: m.content,
                    toolUsageKind: m.tool_usage_kind,
                }))
            ),
        [messages]
    );

    const renderLine = (m: ChatMessage) => (
        <div key={m.id} className="py-1 px-2 hover:bg-white/[0.03] rounded transition-colors">
            <div className="text-[11px] leading-snug flex gap-1.5 items-start font-chat-mono">
                <span className="text-slate-600 shrink-0 tabular-nums">
                    {formatTimestamp(m.timestamp, { timeOnly: true })}
                </span>
                <span className="shrink-0" aria-hidden>
                    {streamActivityIcon(m)}
                </span>
                <div
                    className={`min-w-0 flex-1 break-words ${streamActivityTextClass(m)} max-h-32 overflow-y-auto pr-1`}
                    style={{
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#475569 #0f172a',
                    }}
                >
                    {m.content}
                </div>
            </div>
        </div>
    );

    return (
        <div className="my-0.5">
            <div className="py-1 px-2 text-base text-slate-400 leading-7 font-normal tracking-[0.02em] flex flex-wrap items-center gap-x-1.5 gap-y-1 font-chat">
                <span className="text-slate-500">{formatTimestamp(groupTimestamp)}</span>
                <span className="mx-1 text-slate-600">:</span>
                <span className="text-slate-400">[ASSISTANT]</span>
            </div>
            <div
                className="pl-3 pr-2 py-1 rounded bg-slate-800/40 text-[11px] font-chat-mono text-slate-500 border-l-2 border-slate-600/50"
            >
                AI activity ({headerSummaryLabel})
            </div>

            {olderMessages.length > 0 && (
                <>
                    <button
                        type="button"
                        onClick={toggleEarlier}
                        className="w-full flex items-center gap-1.5 pl-3 pr-2 py-0.5 mt-0.5 rounded bg-slate-800/35 hover:bg-slate-800/55 transition-colors text-[10px] font-chat-mono text-slate-500 hover:text-slate-400 cursor-pointer border-l-2 border-slate-600/40"
                    >
                        <span className="select-none">{earlierExpanded ? '▾' : '▸'}</span>
                        <span>
                            Earlier ({olderMessages.length} message{olderMessages.length > 1 ? 's' : ''})
                        </span>
                    </button>
                    {earlierExpanded && (
                        <div
                            className="ml-3 border-l-2 border-slate-700/60 overflow-y-auto"
                            style={{
                                maxHeight: '200px',
                                scrollbarWidth: 'thin',
                                scrollbarColor: '#475569 #020617',
                            }}
                        >
                            {olderMessages.map(renderLine)}
                        </div>
                    )}
                </>
            )}

            <div
                className={`ml-3 border-l-2 border-slate-700/60 ${olderMessages.length > 0 ? 'mt-0.5' : ''}`}
            >
                {tailMessages.map(renderLine)}
            </div>
        </div>
    );
};

const WaitingForAgentIndicator: React.FC = () => {
    return (
        <div className="mt-2 px-2 py-1.5 text-sm text-slate-400 font-chat">
            <div className="inline-flex items-center rounded-md border border-slate-700/40 bg-slate-800/45 px-2.5 py-1.5">
                <span className="inline-flex items-center gap-1" aria-label="Waiting for agent response">
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-400/90 animate-bounce [animation-delay:-0.24s]" />
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-400/90 animate-bounce [animation-delay:-0.12s]" />
                    <span className="h-1.5 w-1.5 rounded-full bg-slate-400/90 animate-bounce" />
                </span>
            </div>
        </div>
    );
};

const VishCoderChatPanel: React.FC<VishCoderChatPanelProps> = ({ 
    isOpen, 
    onToggle,
    page,
    projectId,
    channel = 'chat',
    className = '' 
}) => {
    const { insetPx } = useBottomPanelInset();
    const { chatMessages, agentConnected, agentProcessing, sendMessage, clearChatHistory, getAgentSocket } =
        useVishCoderChat(page, projectId, channel);
    const [inputValue, setInputValue] = useState('');
    const [showAllLogs, setShowAllLogs] = useState(true);
    const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>([]);
    const [imageErrors, setImageErrors] = useState<Set<string>>(new Set());
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const isConversational = page && page !== 'requirements';
    const discoveryProceedPauseApi = useDiscoveryProceedPauseApi();

    // Use WebSocket status hook for health monitoring
    const wsStatus = useWebSocketStatus();

    // Cleanup ObjectURLs to prevent memory leaks
    // Note: We keep ObjectURLs alive for chat messages, so we clean them up more carefully
    useEffect(() => {
        return () => {
            // Only cleanup pending attachments that haven't been sent yet
            pendingAttachments.forEach(att => {
                if (att.previewUrl.startsWith('blob:')) {
                    URL.revokeObjectURL(att.previewUrl);
                }
            });
        };
    }, [pendingAttachments]);

    const scrollMessagesToBottom = useCallback(() => {
        const el = messagesEndRef.current;
        if (!el) return;
        el.scrollTop = el.scrollHeight;
    }, []);

    // Keep scroll at bottom by default: new messages, panel open, or layout change (e.g. terminal inset).
    useEffect(() => {
        if (!isOpen) return;
        const run = () => scrollMessagesToBottom();
        run();
        const id = window.requestAnimationFrame(() => {
            run();
            window.requestAnimationFrame(run);
        });
        const t = window.setTimeout(run, 50);
        const t2 = window.setTimeout(run, 150);
        return () => {
            window.cancelAnimationFrame(id);
            window.clearTimeout(t);
            window.clearTimeout(t2);
        };
    }, [isOpen, chatMessages, insetPx, scrollMessagesToBottom]);

    // Contract is sent to backend only via "Send to VishCoder" in ContractPopupModal (single path)

    const handleSendMessage = async () => {
        if (!inputValue.trim() && pendingAttachments.length === 0) return;

        const idToken = TokenManager.getIdToken();
        const userId = TokenManager.getUserId();

        let attachmentsToSend: ImageAttachment[];
        try {
            // Upload attachments to S3 first (wait for completion before sending message)
            attachmentsToSend = await Promise.all(
            pendingAttachments.map(async (pending) => {
                // Keep a fresh blob: preview URL for sent message bubbles.
                // This avoids CSP issues where data: URLs may be blocked.
                const messagePreviewUrl = URL.createObjectURL(pending.file);
                let thumbnailDataUrl: string | undefined;
                try {
                    thumbnailDataUrl = await createImageThumbnailDataURL(pending.file);
                } catch (thumbError) {
                    console.warn('Could not create thumbnail for local cache:', thumbError);
                }

                // Upload to S3 if credentials are available
                if (idToken && userId) {
                    try {
                        const uploadResult = await uploadImage(pending.file, {
                            idToken: idToken,
                            userId: userId,
                            projectId: projectId ?? undefined,
                            useS3: true
                        });

                        // Extract S3 path from URL or use key if available
                        let s3Path = uploadResult.key;
                        if (!s3Path && uploadResult.url) {
                            s3Path = extractS3PathFromUrl(uploadResult.url);
                        }

                        const hasRemoteUrl =
                            uploadResult.url.startsWith('https://') ||
                            uploadResult.url.startsWith('http://') ||
                            uploadResult.url.startsWith('s3://');
                        const hasPersistablePath = Boolean(s3Path && s3Path.trim().length > 0);

                        if (!hasRemoteUrl && !hasPersistablePath) {
                            console.warn(
                                `⚠️ Upload for "${pending.name}" did not return an S3 URL/path. Keeping local preview only.`
                            );
                        } else {
                            console.log('✅ Attachment upload result (ready for chat send):', {
                                name: pending.name,
                                s3_path: hasPersistablePath ? s3Path : null,
                                remote_url: hasRemoteUrl ? uploadResult.url : null,
                                has_key: Boolean(uploadResult.key)
                            });
                        }

                        return {
                            kind: 'image' as const,
                            mime: pending.mime,
                            // Keep UI rendering stable even when S3 upload silently falls back.
                            url: hasRemoteUrl ? uploadResult.url : messagePreviewUrl,
                            path: hasPersistablePath ? s3Path : undefined,
                            previewUrl: messagePreviewUrl, // Blob URL for instant display
                            name: pending.name,
                            meta: thumbnailDataUrl ? { thumbnailDataUrl } : undefined
                        };
                    } catch (error) {
                        console.error('S3 upload failed for:', pending.name, error);
                        // Fallback to local blob preview when S3 upload fails
                        return {
                            kind: 'image' as const,
                            mime: pending.mime,
                            url: messagePreviewUrl,
                            previewUrl: messagePreviewUrl,
                            name: pending.name,
                            meta: thumbnailDataUrl ? { thumbnailDataUrl } : undefined
                        };
                    }
                } else {
                    // No credentials, keep local blob preview
                    return {
                        kind: 'image' as const,
                        mime: pending.mime,
                        url: messagePreviewUrl,
                        previewUrl: messagePreviewUrl,
                        name: pending.name,
                        meta: thumbnailDataUrl ? { thumbnailDataUrl } : undefined
                    };
                }
            })
        );
        } catch (err: unknown) {
            const msg = err instanceof Error ? err.message : 'Could not prepare attachments.';
            alert(msg);
            return;
        }

        // Send message with S3 paths (or data URLs as fallback)
        await sendMessage(inputValue.trim(), attachmentsToSend.length > 0 ? attachmentsToSend : undefined);
        
        setInputValue('');
        // Cleanup composer ObjectURLs; sent message bubbles use their own blob URLs.
        pendingAttachments.forEach(att => {
            if (att.previewUrl.startsWith('blob:')) {
                URL.revokeObjectURL(att.previewUrl);
            }
        });
        setPendingAttachments([]);
    };

    const addPendingImageFiles = (files: File[]) => {
        if (files.length === 0) return;

        const newPending: PendingAttachment[] = files
            .filter(file => {
                const validation = validateImageFile(file);
                if (!validation.valid) {
                    alert(validation.error);
                    return false;
                }
                return true;
            })
            .map(file => ({
                clientId: crypto.randomUUID(),
                file,
                previewUrl: URL.createObjectURL(file),
                mime: file.type,
                name: file.name,
                size: file.size,
            }));

        if (newPending.length > 0) {
            setPendingAttachments(prev => [...prev, ...newPending]);
        }
    };

    const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || files.length === 0) return;
        addPendingImageFiles(Array.from(files));
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLTextAreaElement>) => {
        if (agentProcessing || !agentConnected) return;
        const imageFiles = getImageFilesFromClipboardEvent(event.nativeEvent);
        if (imageFiles.length === 0) return;
        event.preventDefault();
        addPendingImageFiles(imageFiles);
    };

    const removeAttachment = (clientId: string) => {
        setPendingAttachments(prev => {
            const attachment = prev.find(att => att.clientId === clientId);
            if (attachment) {
                URL.revokeObjectURL(attachment.previewUrl);
            }
            return prev.filter(att => att.clientId !== clientId);
        });
    };

    // Handle screenshot upload from cache
    const handleScreenshotUpload = async (screenshotId: string, filename?: string) => {
        const dataUrl = getScreenshotFromCache(screenshotId);
        if (!dataUrl) {
            alert('Screenshot not found in cache. It may have expired.');
            return;
        }

        // Convert data URL to File for pending attachment
        const file = new File(
            [await fetch(dataUrl).then(r => r.blob())],
            filename || `Screenshot ${new Date().toISOString().split('T')[0]}.png`,
            { type: 'image/png' }
        );

        const pendingAttachment: PendingAttachment = {
            clientId: crypto.randomUUID(),
            file,
            previewUrl: dataUrl, // Use data URL as preview
            mime: 'image/png',
            name: filename || `Screenshot ${new Date().toISOString().split('T')[0]}.png`,
            size: file.size
        };

        setPendingAttachments(prev => [...prev, pendingAttachment]);
    };

    const renderMessage = (message: ChatMessage, payloadActionsInteractableId: string | null = null) => {
        // Inline discovery controls (create-project flow; registered by ProductDiscoveryChat)
        if (channel === 'discovery' && message.msgType === 'discovery_proceed_pause') {
            const api = discoveryProceedPauseApi;
            if (!api?.decisionPending) {
                return null;
            }
            if (
                api.discoveryActionsMessageId != null &&
                api.discoveryActionsMessageId !== '' &&
                message.id !== api.discoveryActionsMessageId
            ) {
                return null;
            }
            const disabled = !api || api.agentProcessing;
            /** Gate still loading — Guided discovery waits until we know repo/branch/token state. */
            const githubGatePending = api.githubSettingsGateResolved === false;
            const guidedDiscoveryDisabled = disabled || githubGatePending;
            const instantDraftDisabled = disabled;
            const showGithubHint =
                api.githubSettingsGateResolved &&
                api.githubSettingsReady === false &&
                api.githubSettingsHint;
            const githubHintText = api?.githubSettingsHint ?? '';
            const githubHintHasSuperscriptStar = githubHintText.startsWith('*');
            return (
                <div className="px-2 pt-0 pb-2.5 font-chat">
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            type="button"
                            onClick={() => api?.onGuidedDiscovery()}
                            disabled={guidedDiscoveryDisabled}
                            className="rounded-lg bg-indigo-600 px-3 py-2 text-base font-medium text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Guided Discovery
                        </button>
                        <button
                            type="button"
                            onClick={() => api?.onInstantDraft()}
                            disabled={instantDraftDisabled}
                            className="rounded-lg border border-slate-500 bg-slate-800/90 px-3 py-2 text-base font-medium text-slate-100 hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Instant Draft
                        </button>
                    </div>
                    {showGithubHint ? (
                        <p className="mt-2 max-w-md text-sm leading-snug text-amber-200/90">
                            {githubHintHasSuperscriptStar ? (
                                <>
                                    <sup className="mr-0.5">*</sup>
                                    {githubHintText.slice(1)}
                                </>
                            ) : (
                                githubHintText
                            )}
                        </p>
                    ) : null}
                </div>
            );
        }

        // Status messages (for requirements tab)
        if (message.type === 'status') {
            return (
                <div className="py-2.5 px-2 hover:bg-white/[0.03] transition-colors rounded font-chat">
                    <div className="text-base text-slate-400 leading-7 font-normal tracking-[0.02em]">
                        <span className="text-slate-500">
                            {formatTimestamp(message.timestamp)}
                        </span>
                        <span className="mx-1 text-slate-600">:</span>
                        <span className="text-slate-400 font-normal">
                            [{message.agent || 'System'}
                        </span>
                        <span className="text-slate-500">
                            ({message.llm || 'None'})
                        </span>
                        <span className="text-slate-400">]</span>
                        <span className="mx-1 text-slate-600">:</span>
                        <span className="text-slate-300/95">
                            {message.content}
                        </span>
                    </div>
                    {message.progress !== null && message.progress !== undefined && (
                        <div className="mt-1.5 flex items-center gap-2">
                            <div className="flex-1 h-0.5 bg-slate-700/50 rounded-full overflow-hidden">
                                <div 
                                    className={`h-full transition-all duration-500 ${
                                        message.progress === 100 
                                            ? 'bg-gradient-to-r from-emerald-400 to-emerald-500' 
                                            : 'bg-gradient-to-r from-slate-400 to-slate-500'
                                    }`}
                                    style={{ width: `${message.progress}%` }}
                                ></div>
                            </div>
                            <span className="text-slate-500 text-sm tabular-nums">
                                {message.progress}%
                            </span>
                        </div>
                    )}
                </div>
            );
        }

        if (
            message.type === 'assistant' &&
            !message.stream_activity_category &&
            message.msgType === PAYLOAD_MESSAGE_ACTIONS_MSG_TYPE &&
            message.message_actions &&
            message.message_actions.length > 0
        ) {
            const buttonsEnabled =
                payloadActionsInteractableId != null &&
                payloadActionsInteractableId === message.id &&
                !message.actions_used &&
                !agentProcessing;
            return (
                <div className="py-2.5 px-2 hover:bg-white/[0.03] transition-colors rounded font-chat">
                    <div className="text-base text-slate-400 leading-7 font-normal tracking-[0.02em] flex flex-wrap items-center gap-x-1.5 gap-y-1">
                        <span className="text-slate-500">{formatTimestamp(message.timestamp)}</span>
                        <span className="mx-1 text-slate-600">:</span>
                        <span className="text-slate-400">[ASSISTANT]</span>
                    </div>
                    <div className="mt-1.5 min-w-0">
                        <ChatMarkdownBody source={message.content} tone="panel" />
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                        {message.message_actions.map((action) => {
                            const isPrimary = action.style === 'primary';
                            return (
                                <button
                                    key={action.id}
                                    type="button"
                                    disabled={!buttonsEnabled}
                                    onClick={() => {
                                        if (!buttonsEnabled) return;
                                        markChatMessageActionsUsed(message.id);
                                        sendMessage('', undefined, {
                                            messageFieldExtensions: { [action.id]: true },
                                            userBubbleDisplayText: action.label,
                                        });
                                    }}
                                    className={
                                        isPrimary
                                            ? 'rounded-lg bg-indigo-600 px-3 py-2 text-base font-medium text-white shadow-md shadow-indigo-900/40 ring-2 ring-indigo-400/35 hover:bg-indigo-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:ring-0'
                                            : 'rounded-lg border border-slate-500 bg-slate-800/90 px-3 py-2 text-base font-medium text-slate-100 ring-1 ring-slate-400/25 hover:bg-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/50 disabled:opacity-50 disabled:cursor-not-allowed disabled:ring-0'
                                    }
                                >
                                    {action.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            );
        }

        // Conversational messages (for non-requirements tabs)
        if (
            isConversational &&
            (message.conversational || message.attachments || message.blocks) &&
            !(channel === 'chat' && message.type === 'user')
        ) {
            const isUser = message.type === 'user';
            
            return (
                <div className={`mb-4 ${isUser ? 'flex justify-end' : 'flex justify-start'}`}>
                    <div className={`max-w-[85%] rounded-lg px-4 py-3 ${
                        isUser 
                            ? 'bg-purple-50 border border-purple-200' 
                            : 'bg-cyan-50 border border-cyan-200'
                    }`}>
                        {/* Message header */}
                        <div className="flex items-center gap-2 mb-2">
                            <span className={`font-medium text-base ${
                                isUser ? 'text-purple-700/95' : 'text-cyan-800/90'
                            }`}>
                                {isUser ? 'You' : 'VishCoder AI Agent'}
                            </span>
                            {isUser && message.attachments && message.attachments.length > 0 && (
                                <AttachmentSentIndicator count={message.attachments.length} />
                            )}
                            <span className="text-sm text-slate-500">
                                {formatTimestamp(message.timestamp)}
                            </span>
                        </div>

                        {/* Message text */}
                        {message.content && (
                            <div
                                className={`text-base mb-2 leading-relaxed font-normal ${
                                    isUser ? 'text-purple-800/90' : 'text-cyan-900/85'
                                }`}
                            >
                                {isUser ? (
                                    <span className="whitespace-pre-wrap break-words">{message.content}</span>
                                ) : (
                                    <ChatMarkdownBody source={message.content} tone="bubbleAssistant" />
                                )}
                            </div>
                        )}

                        {/* Attachments: thumbnails (data URL / S3) */}
                        {message.attachments && message.attachments.length > 0 && (
                            <div className="space-y-2 mb-2">
                                {message.attachments.map((attachment, idx) => {
                                    if (attachment.kind === 'image') {
                                        const imageKey = `${message.id}-${idx}`;
                                        const hasError = imageErrors.has(imageKey);
                                        
                                        // Use previewUrl (ObjectURL/cache) if available, otherwise use url (S3)
                                        // previewUrl is always available and works instantly
                                        const src = attachment.previewUrl || attachment.meta?.thumbnailDataUrl || attachment.url;
                                        
                                        // Always try to render if we have any source
                                        if (!src) {
                                            return (
                                                <div key={idx} className="h-20 w-32 rounded-lg bg-white/5 flex flex-col items-center justify-center border border-gray-200">
                                                    <svg className="w-6 h-6 text-gray-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                    </svg>
                                                    <span className="text-xs text-slate-500 truncate max-w-[7rem]">{attachment.name || 'Image'}</span>
                                                </div>
                                            );
                                        }
                                        
                                        return (
                                            <div key={idx} className="relative inline-block">
                                                {!hasError ? (
                                                    <img 
                                                        src={src}
                                                        alt={attachment.name || 'Attachment'}
                                                        className="h-10 w-10 rounded object-cover cursor-pointer hover:opacity-90 transition-opacity border border-gray-200"
                                                        onLoad={() => {
                                                            // Clear error state
                                                            setImageErrors(prev => {
                                                                const newSet = new Set(prev);
                                                                newSet.delete(imageKey);
                                                                return newSet;
                                                            });
                                                        }}
                                                        onClick={() => {
                                                            // Open the best available URL
                                                            const urlToOpen = attachment.url || attachment.previewUrl || attachment.meta?.thumbnailDataUrl || src;
                                                            if (urlToOpen.startsWith('data:') || urlToOpen.startsWith('blob:')) {
                                                                // For data/blob URLs, open directly
                                                                window.open(urlToOpen, '_blank');
                                                            } else {
                                                                window.open(urlToOpen, '_blank');
                                                            }
                                                        }}
                                                        onError={(e) => {
                                                            const imgElement = e.currentTarget as HTMLImageElement;
                                                            
                                                            // Try fallback: if url failed, try previewUrl
                                                            if (src === attachment.url && attachment.previewUrl && attachment.previewUrl !== attachment.url) {
                                                                console.log('🔄 S3 URL failed, falling back to preview URL');
                                                                imgElement.src = attachment.previewUrl;
                                                                return;
                                                            }
                                                            
                                                            // Try screenshot cache fallback
                                                            if (attachment.meta?.screenshotId) {
                                                                const cachedDataUrl = getScreenshotFromCache(attachment.meta.screenshotId);
                                                                if (cachedDataUrl) {
                                                                    console.log('🔄 Falling back to cached screenshot');
                                                                    imgElement.src = cachedDataUrl;
                                                                    return;
                                                                }
                                                            }
                                                            
                                                            // Only mark as error if all fallbacks failed
                                                            console.error('❌ All image sources failed for:', attachment.name);
                                                            setImageErrors(prev => new Set(prev).add(imageKey));
                                                        }}
                                                    />
                                                ) : (
                                                    <div className="h-10 w-10 rounded bg-white/5 flex flex-col items-center justify-center border border-gray-200">
                                                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    }
                                    if (attachment.kind === 'code') {
                                        return (
                                            <div key={idx} className="bg-slate-950/80 rounded p-2 text-sm font-chat-mono border border-slate-700/30">
                                                <div className="text-slate-500 mb-1 text-sm font-chat">{attachment.name}</div>
                                                <pre className="text-emerald-400/85 overflow-x-auto text-sm leading-relaxed">
                                                    <code>{attachment.text}</code>
                                                </pre>
                                            </div>
                                        );
                                    }
                                    return null;
                                })}
                            </div>
                        )}

                        {/* Structured blocks (AI responses) */}
                        {message.blocks && message.blocks.length > 0 && (
                            <div className="space-y-2 mt-2">
                                {message.blocks.map((block, idx) => {
                                    // Validate block structure
                                    if (!block || typeof block !== 'object') {
                                        console.warn('Invalid block structure:', block);
                                        return null;
                                    }

                                    // Support both 'kind' and 'type' fields
                                    const blockKind = (block.kind || block.type) as string | undefined;
                                    
                                    if (blockKind === 'summary') {
                                        const blockText = block.text || block.content || '';
                                        return (
                                            <div key={idx} className="bg-blue-50 border-l-4 border-blue-400 p-2 rounded text-base">
                                                <strong className="text-blue-800">Summary:</strong>
                                                <p className="text-blue-700 mt-1">{blockText}</p>
                                            </div>
                                        );
                                    }
                                    if (blockKind === 'steps') {
                                        const items = block.items || block.steps || [];
                                        const blockText = block.text || block.content || '';
                                        return (
                                            <div key={idx} className="bg-green-50 border-l-4 border-green-400 p-2 rounded text-base">
                                                <strong className="text-green-800">Steps:</strong>
                                                {items.length > 0 ? (
                                                    <ol className="list-decimal list-inside mt-1 space-y-1 text-green-700">
                                                        {items.map((item: string, itemIdx: number) => (
                                                            <li key={itemIdx}>{item}</li>
                                                        ))}
                                                    </ol>
                                                ) : (
                                                    <p className="text-green-700 mt-1">{blockText || 'No steps provided'}</p>
                                                )}
                                            </div>
                                        );
                                    }
                                    if (blockKind === 'warning') {
                                        const blockText = block.text || block.content || '';
                                        return (
                                            <div key={idx} className="bg-yellow-50 border-l-4 border-yellow-400 p-2 rounded text-base">
                                                <strong className="text-yellow-800">⚠️ Warning:</strong>
                                                <p className="text-yellow-700 mt-1">{blockText}</p>
                                            </div>
                                        );
                                    }
                                    if (blockKind === 'info') {
                                        const blockText = block.text || block.content || '';
                                        return (
                                            <div key={idx} className="bg-blue-50 border-l-4 border-blue-400 p-2 rounded text-base">
                                                <strong className="text-blue-800">ℹ️ Info:</strong>
                                                <p className="text-blue-700 mt-1">{blockText}</p>
                                            </div>
                                        );
                                    }
                                    if (blockKind === 'code') {
                                        const codeContent = block.code || block.text || block.content || '';
                                        const codeLang = block.lang || block.language || '';
                                        return (
                                            <div key={idx} className="bg-slate-950/80 rounded p-2 text-sm font-chat-mono border border-slate-700/30">
                                                {codeLang && (
                                                    <div className="text-slate-500 mb-1 text-sm font-chat">Language: {codeLang}</div>
                                                )}
                                                <pre className="text-emerald-400/85 overflow-x-auto text-sm leading-relaxed">
                                                    <code>{codeContent}</code>
                                                </pre>
                                            </div>
                                        );
                                    }
                                    
                                    // Fallback for unknown block types
                                    console.warn('Unknown block kind:', blockKind, block);
                                    const fallbackText = block.text || block.content || JSON.stringify(block);
                                    return (
                                        <div key={idx} className="bg-gray-50 border-l-4 border-gray-400 p-2 rounded text-base">
                                            <strong className="text-gray-800">{blockKind || 'Unknown'}:</strong>
                                            <p className="text-gray-700 mt-1">{fallbackText}</p>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            );
        }

        // Contract messages are filtered out and not displayed in chat
        // They are auto-sent to WebSocket but not shown to user

        // Default message format (fallback)
        const isAssistant = message.type === 'assistant';
        return (
            <div className="py-2.5 px-2 hover:bg-white/[0.03] transition-colors rounded font-chat">
                <div className="text-base text-slate-400 leading-7 font-normal tracking-[0.02em] flex flex-wrap items-center gap-x-1.5 gap-y-1">
                    <span className="text-slate-500">
                        {formatTimestamp(message.timestamp)}
                    </span>
                    <span className="mx-1 text-slate-600">:</span>
                    {message.type === 'user' && message.attachments && message.attachments.length > 0 && (
                        <AttachmentSentIndicator count={message.attachments.length} className="text-violet-400/75" />
                    )}
                    <span
                        className={`${
                            message.type === 'user' ? 'text-violet-400/85' : 'text-slate-400'
                        }`}
                    >
                        [{message.type.toUpperCase()}]
                    </span>
                </div>
                <div className="mt-1.5 min-w-0">
                    {isAssistant ? (
                        <ChatMarkdownBody source={message.content} tone="panel" />
                    ) : (
                        <span className="text-base text-violet-300/90 whitespace-pre-wrap break-words">
                            {message.content}
                        </span>
                    )}
                </div>
                {message.attachments && message.attachments.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                        {message.attachments.map((attachment, idx) => {
                            if (attachment.kind !== 'image') return null;
                            const src = attachment.previewUrl || attachment.meta?.thumbnailDataUrl || attachment.url;
                            if (!src) return null;
                            return (
                                <img
                                    key={idx}
                                    src={src}
                                    alt={attachment.name || 'Attachment'}
                                    className="h-10 w-10 rounded object-cover border border-gray-600 cursor-pointer"
                                    onClick={() => {
                                        const urlToOpen = attachment.url || attachment.previewUrl || src;
                                        window.open(urlToOpen, '_blank');
                                    }}
                                />
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    if (!isOpen) return null;

    return (
        <div
            className={`fixed right-0 top-0 w-[37.5%] min-w-[480px] max-w-[600px] bg-slate-900/95 border-l border-slate-700/40 flex flex-col transform transition-all duration-300 ease-in-out z-40 font-chat subpixel-antialiased ${className}`}
            style={{ bottom: insetPx }}
        >
            {/* Header */}
            <div className="p-4 border-b border-slate-700/40 shrink-0">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <h3 className="text-slate-200 font-medium text-base tracking-wide">VishCoder AI Agent</h3>
                        <div className={`w-2 h-2 rounded-full ${agentConnected ? 'bg-emerald-500/80' : 'bg-rose-500/70'}`}></div>
                    </div>
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={clearChatHistory}
                            className="text-slate-500 hover:text-rose-300/90 px-2 py-1 rounded text-sm font-normal transition-colors border border-slate-600/60 hover:border-rose-400/40"
                            title="Delete History & Clear Cache"
                        >
                            Delete History
                        </button>
                        <button
                            onClick={onToggle}
                            className="text-slate-500 hover:text-slate-300 p-1"
                            title="Close Panel"
                        >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L6 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>
                <div className="text-sm text-slate-500 mt-1 font-normal" style={{ fontStyle: 'italic' }}>
                    {agentConnected ? (
                        <span className="text-emerald-400/85">● Connected</span>
                    ) : (
                        <span className="text-rose-400/80">● Disconnected</span>
                    )}
                    {(() => {
                        const visibleMessages = chatMessages.filter(m => !m.isContract);
                        return visibleMessages.length > 0 && (
                            <span className="ml-2 text-slate-500">({visibleMessages.length} messages)</span>
                        );
                    })()}
                </div>
            </div>
            
            {/* Chat Messages */}
            <div 
                ref={messagesEndRef}
                className="flex-1 min-h-0 overflow-y-auto p-3 bg-slate-900/90 border-b border-slate-700/40 chat-messages-container relative"
                style={{ 
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#475569 #020617',
                    overflowY: 'auto'
                }}
            >
                {(() => {
                    // Only show messages from 'chat' channel (filter out any messages from other channels)
                    const visibleMessages = chatMessages.filter(m => 
                        !m.isContract &&
                        m.channel === channel &&
                        // Discovery doc summaries are for live document state, not chat transcript UI.
                        m.msgType !== 'doc_summary'
                    );
                    /** Discovery can briefly hold duplicate proceed/pause rows (re-bootstrap / DB); show one row. */
                    let forDisplay = visibleMessages;
                    if (channel === 'discovery') {
                        const pauseRows = visibleMessages.filter((m) => m.msgType === 'discovery_proceed_pause');
                        if (pauseRows.length > 1) {
                            const keepId =
                                discoveryProceedPauseApi?.discoveryActionsMessageId &&
                                pauseRows.some((p) => p.id === discoveryProceedPauseApi.discoveryActionsMessageId)
                                    ? discoveryProceedPauseApi.discoveryActionsMessageId
                                    : pauseRows[pauseRows.length - 1].id;
                            forDisplay = visibleMessages.filter(
                                (m) => m.msgType !== 'discovery_proceed_pause' || m.id === keepId
                            );
                        }
                    }
                    if (forDisplay.length === 0) {
                        return (
                            <div className="text-center text-slate-500 text-sm py-8 font-chat">
                                <div className="mb-2 text-slate-400 text-base font-normal">[INFO] No messages yet</div>
                                <div className="text-slate-500 text-base font-normal">
                                    Start a conversation with VishCoder AI Agent. You can upload images too!
                                </div>
                            </div>
                        );
                    }
                    return (
                        <div className="pb-2">
                            {/* Status message toggle */}
                            {forDisplay.filter(m => m.type === 'status').length > 0 && (
                                <div className="flex items-center justify-between py-2 px-1 mb-3">
                                    <div className="text-sm text-slate-500 font-chat font-normal">
                                        [{forDisplay.filter(m => m.type === 'status').length} status updates]
                                    </div>
                                    <button
                                        onClick={() => setShowAllLogs(!showAllLogs)}
                                        className="text-sm text-slate-500 hover:text-slate-400 px-2 py-1 rounded transition-colors font-chat font-normal"
                                    >
                                        {showAllLogs ? '[collapse]' : '[expand]'}
                                    </button>
                                </div>
                            )}
                            <div className="space-y-1">
                                {(() => {
                                    const filtered = forDisplay.filter((message) => {
                                        if (message.type !== 'status') return true;
                                        if (showAllLogs) return true;
                                        const statusMessages = forDisplay.filter(m => m.type === 'status');
                                        return statusMessages.length > 0 && message.id === statusMessages[statusMessages.length - 1].id;
                                    });
                                    const localUserId = TokenManager.getUserId() ?? null;
                                    const sessionTranscript = filtered.filter((m) =>
                                        chatMessageMatchesPanelSession(m, channel, page, projectId, localUserId)
                                    );
                                    const payloadActionsInteractableId =
                                        getActivePayloadActionsMessageId(sessionTranscript);
                                    const displayItems = buildChatDisplayItems(filtered);
                                    const awaitingAgentResponse =
                                        filtered.length > 0 && filtered[filtered.length - 1].type === 'user';
                                    return displayItems.map((item, idx) => {
                                        if (item.kind === 'stream_group') {
                                            return (
                                                <div key={item.id}>
                                                    <ChatStreamActivityGroup messages={item.messages} />
                                                </div>
                                            );
                                        }
                                        const message = item.message;
                                        const prev = idx > 0 ? lastMessageInDisplayItem(displayItems[idx - 1]) : undefined;
                                        const tightToAssistant =
                                            channel === 'discovery' &&
                                            message.msgType === 'discovery_proceed_pause' &&
                                            prev?.type === 'assistant';
                                        return (
                                            <div
                                                key={message.id}
                                                className={
                                                    tightToAssistant ? '-mt-2.5' : undefined
                                                }
                                            >
                                                {renderMessage(message, payloadActionsInteractableId)}
                                            </div>
                                        );
                                    }).concat(
                                        awaitingAgentResponse
                                            ? [<WaitingForAgentIndicator key="waiting-agent-response" />]
                                            : []
                                    );
                                })()}
                            </div>
                        </div>
                    );
                })()}
            </div>
            
            {/* Chat Input */}
            <div className="p-3 border-t border-slate-700/40 shrink-0">
                {/* Attachments preview - tiny thumbnails using ObjectURL */}
                {pendingAttachments.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-2">
                        {pendingAttachments.map((attachment) => (
                            <div key={attachment.clientId} className="relative inline-block">
                                <img 
                                    src={attachment.previewUrl} 
                                    alt={attachment.name}
                                    className="h-10 w-10 rounded object-cover border border-gray-600"
                                />
                                <button
                                    onClick={() => removeAttachment(attachment.clientId)}
                                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs hover:bg-red-600 transition-colors"
                                    title="Remove attachment"
                                >
                                    ×
                                </button>
                            </div>
                        ))}
                    </div>
                )}

                <div className="relative">
                    <textarea
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onPaste={handlePaste}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSendMessage();
                            }
                        }}
                        placeholder="Chat with VishCoder AI Agent... (upload or paste images)"
                        disabled={agentProcessing || !agentConnected}
                        className="w-full bg-slate-800/50 text-slate-300 placeholder-slate-500 rounded-lg px-4 py-3 pl-12 pr-20 text-base leading-relaxed focus:outline-none focus:ring-1 focus:ring-slate-500/50 disabled:opacity-50 disabled:cursor-not-allowed resize-none"
                        style={{ 
                            height: '132px',
                            minHeight: '132px'
                        }}
                    />
                    
                    {/* Image upload button - bottom left */}
                    <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        disabled={agentProcessing || !agentConnected}
                        className="absolute left-3 bottom-3 p-2 text-slate-500 hover:text-slate-300 hover:bg-slate-700/30 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors z-10"
                        title="Upload image or paste from clipboard (Ctrl/Cmd+V)"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                    </button>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                        className="hidden"
                    />

                    <button
                        onClick={handleSendMessage}
                        disabled={(!inputValue.trim() && pendingAttachments.length === 0) || agentProcessing || !agentConnected}
                        className="absolute right-3 bottom-3 p-2 bg-slate-600/85 text-slate-200 rounded-lg hover:bg-slate-500/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        title="Send message"
                    >
                        {agentProcessing ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                            </svg>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default VishCoderChatPanel;
